import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { AdSlotProps, AdSlotState, AdError, AdErrorType } from '../../types/ads';
import { adManager } from '../../services/AdManager';
import { adAnalytics } from '../../services/AdAnalytics';
import { getAdSlotConfig } from '../../config/adConfig';
import { pwaAdService } from '../../services/PWAAdService';
import { adServiceWorkerIntegration } from '../../utils/adServiceWorkerIntegration';
import { usePrivacy } from '../../contexts/PrivacyContext';
import { Box, Typography, Button } from '@mui/material';

/**
 * Base AdSlot component for displaying advertisements with lazy loading and error handling
 * Supports Prebid.js header bidding and Google Ad Manager integration
 */
const AdSlot: React.FC<AdSlotProps> = ({
  slotId,
  sizes,
  targeting = {},
  className = '',
  lazy = true,
  refreshInterval,
  onLoad,
  onError
}) => {
  const adRef = useRef<HTMLDivElement>(null);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const refreshTimerRef = useRef<NodeJS.Timeout | null>(null);
  const viewabilityObserverRef = useRef<IntersectionObserver | null>(null);
  const loadStartTime = useRef<number>(0);
  const isLoadingRef = useRef<boolean>(false);
  const isMountedRef = useRef<boolean>(true);
  
  const { canShowAds, canShowPersonalizedAds, refreshConsent } = usePrivacy();
  
  const [state, setState] = useState<AdSlotState>({
    isLoaded: false,
    isVisible: false,
    error: undefined,
    revenue: undefined
  });

  // Memoize targeting to prevent unnecessary re-renders
  const memoizedTargeting = useMemo(() => targeting, [JSON.stringify(targeting)]);

  /**
   * Handle ad loading errors with proper error reporting
   */
  const handleError = useCallback((errorType: AdErrorType, message: string, context?: any) => {
    if (!isMountedRef.current) return;
    
    const error: AdError = {
      type: errorType,
      slotId,
      message,
      timestamp: new Date(),
      context
    };

    setState(prev => ({ ...prev, error }));
    onError?.(error);

    // Track error with analytics
    try {
      adAnalytics.trackError(error);
    } catch (analyticsError) {
      console.warn('Failed to track ad error:', analyticsError);
    }

    // Log error for monitoring
    console.error(`AdSlot Error [${slotId}]:`, error);
  }, [slotId, onError]);

  /**
   * Set up AdManager error handling
   */
  useEffect(() => {
    const errorHandler = (error: AdError) => {
      if (!isMountedRef.current || error.slotId !== slotId) return;
      
      setState(prev => ({ ...prev, error }));
      onError?.(error);
    };

    // Only set up error handler if adManager has the method
    if (adManager && typeof adManager.onError === 'function') {
      adManager.onError(errorHandler);
    }
    
    // Cleanup function - even though AdManager doesn't provide removal,
    // we can at least clear our reference
    return () => {
      // No cleanup available for AdManager error handlers
    };
  }, [slotId, onError]);

  /**
   * Load and display the ad using AdManager
   */
  const loadAd = useCallback(async () => {
    if (!isMountedRef.current || !adRef.current || state.isLoaded || isLoadingRef.current) {
      return;
    }

    // Check privacy compliance before loading ads
    if (!canShowAds) {
      const privacyError: AdError = {
        type: AdErrorType.BLOCKED,
        slotId,
        message: 'Ads blocked due to privacy settings',
        timestamp: new Date(),
        context: { reason: 'privacy_consent_denied' }
      };
      if (isMountedRef.current) {
        setState(prev => ({ ...prev, error: privacyError }));
      }
      return;
    }

    isLoadingRef.current = true;

    try {
      loadStartTime.current = Date.now();
      
      // Get slot configuration from config or use props
      const slotConfig = getAdSlotConfig(slotId);
      const finalSizes = slotConfig?.sizes || sizes;
      const finalTargeting = { ...slotConfig?.targeting, ...memoizedTargeting };

      // Register the slot with AdManager if not already registered
      if (slotConfig) {
        adManager.registerAdSlot({
          ...slotConfig,
          sizes: finalSizes,
          targeting: finalTargeting,
          refreshRate: refreshInterval || slotConfig.refreshRate || 0
        });
      } else {
        // Register with props if no config found
        adManager.registerAdSlot({
          slotId,
          sizes: finalSizes,
          targeting: finalTargeting,
          refreshRate: refreshInterval || 0,
          lazy: lazy ?? true,
          priority: 1
        });
      }

      // Initialize AdManager if not already initialized
      if (!(adManager as any).isInitialized) {
        await adManager.initializeAds();
      }

      // Check if still mounted before proceeding
      if (!isMountedRef.current) return;

      // Load the ad slot (tries custom ad server first, then GAM/Prebid)
      await adManager.loadAdSlot(slotId, finalTargeting);

      if (!isMountedRef.current) return;

      const loadTime = Date.now() - loadStartTime.current;
      
      // Check if the container actually has ad content
      const hasContent = adRef.current && adRef.current.innerHTML.trim().length > 0 
        && !adRef.current.innerHTML.includes('Loading advertisement');
      
      setState(prev => ({ ...prev, isLoaded: true }));
      
      if (hasContent) {
        onLoad?.();
      }

      // Cache ad for offline use if PWA features are enabled
      if (adRef.current && adRef.current.innerHTML) {
        try {
          await adServiceWorkerIntegration.cacheAdCreative(
            `${window.location.origin}/ad/${slotId}`,
            adRef.current.innerHTML,
            {
              slotId,
              targeting: finalTargeting,
              loadTime,
              timestamp: Date.now(),
              userAgent: navigator.userAgent
            }
          );
        } catch (cacheError) {
          console.warn('Failed to cache ad creative:', cacheError);
        }
      }

      // Track impression with analytics
      try {
        adAnalytics.trackAdImpression(slotId, {
          filled: true,
          viewable: false, // Will be updated by viewability observer
          loadTime,
          targeting: finalTargeting,
          advertiser: 'Unknown', // Will be updated when ad metadata is available
          campaign: 'Unknown',
          pwaEnabled: pwaAdService.isInstalled(),
          offline: pwaAdService.isOfflineMode()
        });
      } catch (analyticsError) {
        console.warn('Failed to track ad impression:', analyticsError);
      }

    } catch (error) {
      if (!isMountedRef.current) return;

      // Try to show offline fallback if available
      if (pwaAdService.isOfflineMode() && adRef.current) {
        try {
          const fallbackHtml = await pwaAdService.getOfflineAdFallback({
            slotId,
            sizes: sizes,
            targeting: memoizedTargeting,
            refreshRate: refreshInterval || 0,
            lazy: lazy ?? true,
            priority: 1
          });
          
          if (isMountedRef.current && adRef.current) {
            adRef.current.innerHTML = fallbackHtml;
            setState(prev => ({ ...prev, isLoaded: true }));
            onLoad?.();
            
            // Track fallback usage
            try {
              adAnalytics.trackAdImpression(slotId, {
                filled: true,
                viewable: false,
                loadTime: Date.now() - loadStartTime.current,
                targeting: memoizedTargeting,
                advertiser: 'Fallback',
                campaign: 'Offline',
                fallback: true
              });
            } catch (analyticsError) {
              console.warn('Failed to track fallback impression:', analyticsError);
            }
          }
          
          return;
        } catch (fallbackError) {
          console.warn('Failed to load offline fallback:', fallbackError);
        }
      }
      
      handleError(
        AdErrorType.NETWORK_ERROR,
        `Failed to load ad: ${error instanceof Error ? error.message : 'Unknown error'}`,
        error
      );
    } finally {
      isLoadingRef.current = false;
    }
  }, [slotId, sizes, memoizedTargeting, refreshInterval, lazy, state.isLoaded, handleError, onLoad, canShowAds]);

  /**
   * Set up intersection observer for lazy loading
   */
  useEffect(() => {
    if (!lazy || !adRef.current || typeof IntersectionObserver === 'undefined') return;

    try {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          if (!isMountedRef.current) return;
          
          entries.forEach((entry) => {
            if (entry.isIntersecting && !state.isVisible) {
              setState(prev => ({ ...prev, isVisible: true }));
              loadAd();
            }
          });
        },
        {
          rootMargin: '50px',
          threshold: 0.1
        }
      );

      if (observerRef.current && adRef.current) {
        observerRef.current.observe(adRef.current);
      }
    } catch (error) {
      // Fallback: load ad immediately if IntersectionObserver fails
      // Only in production environments, not in tests
      if (isMountedRef.current && process.env.NODE_ENV !== 'test') {
        setState(prev => ({ ...prev, isVisible: true }));
        loadAd();
      }
    }

    return () => {
      if (observerRef.current && typeof observerRef.current.disconnect === 'function') {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
    };
  }, [lazy, state.isVisible]); // Removed loadAd from dependencies to prevent infinite loop

  /**
   * Load ad immediately if not lazy loading
   */
  useEffect(() => {
    if (!lazy && isMountedRef.current) {
      loadAd();
    }
  }, [lazy]); // Removed loadAd from dependencies to prevent infinite loop

  /**
   * Set up viewability tracking
   */
  useEffect(() => {
    if (!state.isLoaded || !adRef.current || typeof IntersectionObserver === 'undefined') return;

    try {
      viewabilityObserverRef.current = new IntersectionObserver(
        (entries) => {
          if (!isMountedRef.current) return;
          
          entries.forEach((entry) => {
            const isViewable = entry.intersectionRatio >= 0.5; // 50% viewable threshold
            
            if (isViewable) {
              // Track viewable impression
              try {
                adAnalytics.trackAdImpression(slotId, {
                  filled: true,
                  viewable: true,
                  targeting: memoizedTargeting
                });
              } catch (analyticsError) {
                console.warn('Failed to track viewable impression:', analyticsError);
              }
            }
          });
        },
        { threshold: [0.5] }
      );

      if (viewabilityObserverRef.current && adRef.current) {
        viewabilityObserverRef.current.observe(adRef.current);
      }
    } catch (error) {
      console.warn('Viewability tracking failed:', error);
    }

    return () => {
      if (viewabilityObserverRef.current && typeof viewabilityObserverRef.current.disconnect === 'function') {
        viewabilityObserverRef.current.disconnect();
        viewabilityObserverRef.current = null;
      }
    };
  }, [state.isLoaded, slotId, memoizedTargeting]);

  /**
   * Set up click tracking
   */
  useEffect(() => {
    if (!state.isLoaded || !adRef.current) return;

    const handleClick = (event: MouseEvent) => {
      if (!isMountedRef.current) return;
      
      // Check if click is on an ad element
      const target = event.target as HTMLElement;
      const adElement = target.closest('a, [data-ad-click]');
      
      if (adElement) {
        const clickUrl = adElement.getAttribute('href') || 
                        adElement.getAttribute('data-click-url') || '';
        
        try {
          adAnalytics.trackAdClick(slotId, {
            revenue: 0, // Will be updated by ad server callback if available
            advertiser: adElement.getAttribute('data-advertiser') || 'Unknown',
            campaign: adElement.getAttribute('data-campaign') || 'Unknown',
            clickUrl
          });
        } catch (analyticsError) {
          console.warn('Failed to track ad click:', analyticsError);
        }
      }
    };

    const currentRef = adRef.current;
    if (currentRef) {
      currentRef.addEventListener('click', handleClick);
    }

    return () => {
      if (currentRef) {
        currentRef.removeEventListener('click', handleClick);
      }
    };
  }, [state.isLoaded, slotId]);

  /**
   * Set up refresh interval if specified
   */
  useEffect(() => {
    if (refreshInterval && state.isLoaded) {
      refreshTimerRef.current = setInterval(() => {
        if (!isMountedRef.current) return;
        
        if (window.googletag?.cmd) {
          window.googletag.cmd.push(() => {
            if (window.googletag?.pubads) {
              window.googletag.pubads().refresh();
            }
          });
        }
        
        // Track refresh as new impression
        try {
          adAnalytics.trackAdImpression(slotId, {
            filled: true,
            viewable: true,
            targeting: memoizedTargeting
          });
        } catch (analyticsError) {
          console.warn('Failed to track refresh impression:', analyticsError);
        }
      }, refreshInterval * 1000);

      return () => {
        if (refreshTimerRef.current) {
          clearInterval(refreshTimerRef.current);
          refreshTimerRef.current = null;
        }
      };
    }
  }, [refreshInterval, state.isLoaded, slotId, memoizedTargeting]);

  /**
   * Cleanup on unmount
   */
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      
      // Clean up intersection observers
      if (observerRef.current && typeof observerRef.current.disconnect === 'function') {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      if (viewabilityObserverRef.current && typeof viewabilityObserverRef.current.disconnect === 'function') {
        viewabilityObserverRef.current.disconnect();
        viewabilityObserverRef.current = null;
      }
      
      // Clean up refresh timer
      if (refreshTimerRef.current) {
        clearInterval(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      
      // Clean up ad slot using AdManager
      if (state.isLoaded && adManager && typeof adManager.destroyAd === 'function') {
        adManager.destroyAd(slotId).catch(error => {
          console.error(`Error destroying ad slot ${slotId}:`, error);
        });
      }
    };
  }, []); // Empty dependency array - only run on mount/unmount

  /**
   * Render privacy compliance message if ads are not allowed
   */
  if (!canShowAds) {
    return (
      <Box
        className={`ad-slot ad-slot-privacy ${className}`}
        data-testid={`ad-slot-privacy-${slotId}`}
        sx={{
          minHeight: sizes[0] ? `${sizes[0][1]}px` : '250px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'background.paper',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          p: 2,
          textAlign: 'center'
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Ads are currently disabled
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ mb: 2 }}>
          You can enable ads in your privacy settings
        </Typography>
        <Button 
          size="small" 
          variant="outlined" 
          onClick={refreshConsent}
          sx={{ fontSize: '0.75rem' }}
        >
          Update Preferences
        </Button>
      </Box>
    );
  }

  /**
   * Render error state
   */
  if (state.error) {
    return (
      <div 
        className={`ad-slot ad-slot-error ${className}`}
        data-testid={`ad-slot-error-${slotId}`}
        style={{
          minHeight: sizes[0] ? `${sizes[0][1]}px` : '250px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: 'transparent',
          border: '1px dashed',
          borderColor: 'divider',
          color: '#666',
          fontSize: '14px'
        }}
      >
        <span>Advertisement unavailable</span>
      </div>
    );
  }

  /**
   * Render loading state
   */
  if (lazy && !state.isVisible) {
    return (
      <div 
        ref={adRef}
        className={`ad-slot ad-slot-placeholder ${className}`}
        data-testid={`ad-slot-placeholder-${slotId}`}
        style={{
          minHeight: sizes[0] ? `${sizes[0][1]}px` : '250px',
          backgroundColor: 'transparent',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#999',
          fontSize: '12px'
        }}
      >
        <span>Loading advertisement...</span>
      </div>
    );
  }

  /**
   * Render ad slot
   */
  return (
    <div 
      ref={adRef}
      id={slotId}
      className={`ad-slot ${className}`}
      data-testid={`ad-slot-${slotId}`}
      style={{
        minHeight: sizes[0] ? `${sizes[0][1]}px` : '250px',
        textAlign: 'center'
      }}
    />
  );
};

export default AdSlot;